import {
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUS_LABELS,
  type SubscriptionStatus,
} from '@shared/constants/subscriptionStatus';
import type { UserResponse } from '@shared/schemas';

/**
 * Where a user stands on the platform, as one value: their access status, or
 * `deactivated` once the account is archived (#573).
 *
 * Archiving wins over the status. An archived account cannot sign in, whatever
 * `subscription_status` still says, so calling it "Active" would state the
 * opposite of the truth.
 */
export type AccessState = SubscriptionStatus | 'deactivated';

export const DEACTIVATED = 'deactivated' as const;

/** A status is a plain string on the wire; anything unmapped reads through as-is. */
type AccessRow = { subscription_status: UserResponse['subscription_status'] | string } & {
  archived_at?: string | null;
};

export function accessState(row: AccessRow): AccessState {
  if (row.archived_at) return DEACTIVATED;
  return row.subscription_status as SubscriptionStatus;
}

/** The chips' words; the filter and the summary line use them too. */
export const ACCESS_STATE_LABELS: Record<AccessState, string> = {
  ...SUBSCRIPTION_STATUS_LABELS,
  [DEACTIVATED]: 'Deactivated',
};

/** Reading order: who is in, who is on the way, who is not, who was switched off. */
export const ACCESS_STATE_ORDER: readonly AccessState[] = [
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.PENDING,
  SUBSCRIPTION_STATUS.NO_ACCESS,
  SUBSCRIPTION_STATUS.EXPIRED,
  DEACTIVATED,
];

/** True when the row is in one of `states`. No state picked means no filter. */
export function matchesAccessStates(row: AccessRow, states: readonly AccessState[]): boolean {
  return states.length === 0 || states.includes(accessState(row));
}
